import { syncAPI } from 'api/sync'
import type { PushOfflineOrderResult } from 'api/sync'
import { logPosAuditEvent } from 'services/posAuditLog'
import { publishPosDeviceSyncHeartbeat } from 'services/posDeviceSyncHeartbeat'
import * as posStore from 'store/posStore'
import { replayQueue } from 'slices/offlineMiddleware'
import { store } from 'slices/index'
import { fetchCategories } from 'slices/categoriesSlice'
import { fetchProducts } from 'slices/productsSlice'
import { fetchCustomers } from 'slices/customersSlice'
import { fetchSuppliers } from 'slices/suppliersSlice'
import { fetchPurchases } from 'slices/purchasesSlice'
import { fetchExpenses } from 'slices/expensesSlice'
import { fetchDeliveries } from 'slices/deliveriesSlice'
import { checkConnectivity, getLastKnownOnline } from 'utils/connectivityCheck'
import { flushTableOps } from 'services/TableSyncService'
import showWarning from 'helpers/WarningNotification'
import { isSuspiciousOfflineSyncRow } from 'utils/offlineSyncMismatch'
import i18n from 'i18next'

let pushIntervalId: ReturnType<typeof setInterval> | null = null
let onlineHandler: (() => void) | null = null
let syncing = false
let pushMutex: Promise<unknown> = Promise.resolve()
let lastModuleSyncAt = 0

const SYNCING_STALE_MS = 5 * 60 * 1000
/** Push / heartbeat / queue flush cadence */
const AUTO_SYNC_INTERVAL_MS = 60_000
const AUTO_SYNC_JITTER_MS = 15_000
/** Full Redux list fan-out is much heavier — run rarely and only from the leader tab */
const MODULE_SYNC_INTERVAL_MS = 5 * 60_000
const TAB_LOCK_KEY = 'erp_sync_leader_tab'
const TAB_LOCK_STALE_MS = 90_000

function getTabId(): string {
  try {
    const existing = sessionStorage.getItem('erp_tab_id')
    if (existing) return existing
    const id = crypto.randomUUID()
    sessionStorage.setItem('erp_tab_id', id)
    return id
  } catch {
    return 'fallback-tab'
  }
}

/** Only one open shell should hammer the 7 list endpoints. */
function claimSyncLeaderTab(): boolean {
  try {
    const id = getTabId()
    const now = Date.now()
    const raw = localStorage.getItem(TAB_LOCK_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as { id?: string; at?: number }
      if (parsed?.id && parsed.id !== id && typeof parsed.at === 'number' && now - parsed.at < TAB_LOCK_STALE_MS) {
        return false
      }
    }
    localStorage.setItem(TAB_LOCK_KEY, JSON.stringify({ id, at: now }))
    return true
  } catch {
    return true
  }
}

function shouldRunModuleFanOut(): boolean {
  if (typeof document !== 'undefined' && document.hidden) return false
  if (!claimSyncLeaderTab()) return false
  const now = Date.now()
  if (now - lastModuleSyncAt < MODULE_SYNC_INTERVAL_MS) return false
  lastModuleSyncAt = now
  return true
}

function withPushMutex<T>(fn: () => Promise<T>): Promise<T> {
  const run = pushMutex.then(fn, fn)
  pushMutex = run.then(
    () => undefined,
    () => undefined
  )
  return run
}

function isSyncingStale(order: posStore.PendingOfflineOrder): boolean {
  if (order.status !== 'syncing') return false
  if (!order.created_at_local) return true
  return Date.now() - new Date(order.created_at_local).getTime() > SYNCING_STALE_MS
}

export async function pull(branchId?: string | null): Promise<void> {
  if (!(await checkConnectivity())) return
  const res = await syncAPI.pullFullPosData(branchId ?? undefined)
  if (res.status === 'success' && res.data) {
    await posStore.setCatalog(res.data, { branchId: res.data.branch_id ?? branchId ?? undefined })
  }
}

async function pushOrdersBatch(
  orders: posStore.PendingOfflineOrder[]
): Promise<{ pushed: number; errors: string[]; rows: PushOfflineOrderResult[] }> {
  if (!(await checkConnectivity())) {
    return { pushed: 0, errors: [], rows: [] }
  }

  const errors: string[] = []
  const done = new Set<string>()
  const rows: PushOfflineOrderResult[] = []
  const toSend: posStore.PendingOfflineOrder[] = []
  const dropBroken = new Set<string>()
  const shiftIdUpdates = new Map<string, string>()

  for (const raw of orders) {
    if (raw.status === 'syncing' && !isSyncingStale(raw)) {
      continue
    }
    const fixed = posStore.coercePendingOrderForSync(raw)
    if (!fixed) {
      const brokenReason =
        'طلب بدون بنود صالحة — يُحفظ في الطلبات الفاشلة (لا يمكن إصلاحه للمزامنة)'
      const id =
        typeof (raw as { client_uuid?: string })?.client_uuid === 'string'
          ? (raw as { client_uuid: string }).client_uuid
          : ''
      if (id) {
        dropBroken.add(id)
        if (raw && typeof raw === 'object' && (raw as posStore.PendingOfflineOrder).client_uuid) {
          await posStore.appendFailedOrder(raw as posStore.PendingOfflineOrder, brokenReason, 0)
          await logPosAuditEvent(id, 'push_failed', { reason: 'coerce_null' })
        }
      }
      errors.push(`${id || '—'}: ${brokenReason}`)
      continue
    }
    const enriched = await posStore.backfillShiftIdForSync(fixed, fixed.branch_id)
    if (enriched.shift_id && enriched.shift_id !== fixed.shift_id) {
      shiftIdUpdates.set(enriched.client_uuid, enriched.shift_id)
    }
    toSend.push(enriched)
  }

  if (shiftIdUpdates.size > 0) {
    await posStore.patchPendingOrdersShiftIds(shiftIdUpdates)
  }

  if (dropBroken.size > 0) {
    await posStore.removePendingByClientUuids(dropBroken)
  }

  if (toSend.length === 0) {
    return { pushed: 0, errors, rows }
  }

  try {
    const failed = new Set<string>()
    const ordersByBranch = new Map<string, posStore.PendingOfflineOrder[]>()
    for (const order of toSend) {
      const branchId = String(order.branch_id || '').trim()
      if (!branchId) {
        errors.push(`${order.client_uuid}: طلب بدون فرع — لا يمكن مزامنته بأمان`)
        failed.add(order.client_uuid)
        await posStore.appendFailedOrder(order, 'Missing branch_id', 0)
        continue
      }
      const claimed = await posStore.markPendingOrderSyncing(order.client_uuid)
      if (!claimed) {
        continue
      }
      const bucket = ordersByBranch.get(branchId) ?? []
      bucket.push(order)
      ordersByBranch.set(branchId, bucket)
    }

    for (const [branchId, branchOrders] of Array.from(ordersByBranch.entries())) {
      if (branchOrders.length === 0) continue
      try {
        const res = await syncAPI.pushOfflineOrders(branchOrders, branchId)
        if (res.status !== 'success' || !Array.isArray(res.data)) {
          errors.push(res.message || `فشل مزامنة الفرع ${branchId}: استجابة غير صالحة من الخادم`)
          for (const order of branchOrders) {
            await posStore.resetPendingOrderStatus(order.client_uuid, 'pending')
          }
          continue
        }
        for (const row of res.data) {
          rows.push(row)
          if (row.status === 'created' || row.status === 'duplicate') {
            done.add(row.client_uuid)
            await logPosAuditEvent(row.client_uuid, 'push_confirmed', { status: row.status })
            const matchedOrder = branchOrders.find(
              (o: posStore.PendingOfflineOrder) => o.client_uuid === row.client_uuid
            )
            if (isSuspiciousOfflineSyncRow(row, matchedOrder?.print_sequence)) {
              showWarning(
                i18n.t('pos.cart.offlineSyncPrintSequenceMismatch', {
                  localSeq: matchedOrder?.print_sequence,
                  serverSeq: row.print_sequence
                }) as string
              )
            }
          } else if ((row.status === 'error' || row.status === 'conflict') && row.message) {
            const isConflict = row.status === 'conflict' || Boolean(row.code)
            errors.push(`${row.client_uuid}: ${row.message}`)
            const failedOrder = branchOrders.find(
              (o: posStore.PendingOfflineOrder) => o.client_uuid === row.client_uuid
            )
            if (failedOrder) {
              failed.add(row.client_uuid)
              const annotated = {
                ...failedOrder,
                status: isConflict ? ('conflict' as const) : ('failed' as const),
                conflict_code: row.code ?? null,
                last_error: row.message
              }
              await posStore.appendFailedOrder(annotated, row.message, 0)
            } else {
              // Deterministic conflicts must not stay in infinite auto-retry as "pending".
              await posStore.resetPendingOrderStatus(
                row.client_uuid,
                isConflict ? 'conflict' : 'pending'
              )
            }
          } else if (row.client_uuid) {
            errors.push(
              `${row.client_uuid}: استجابة غير متوقعة من الخادم (${row.status ?? 'unknown'})`
            )
            await posStore.resetPendingOrderStatus(row.client_uuid, 'pending')
          }
        }
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined
        errors.push(msg || (e instanceof Error ? e.message : 'sync failed'))
        for (const order of branchOrders) {
          await posStore.resetPendingOrderStatus(order.client_uuid, 'pending')
        }
      }
    }
    const toRemove = new Set<string>()
    done.forEach((u) => toRemove.add(u))
    failed.forEach((u) => toRemove.add(u))
    await posStore.removePendingByClientUuids(toRemove)
    await publishPosDeviceSyncHeartbeat()
    return { pushed: done.size, errors, rows }
  } catch (e: unknown) {
    const msg =
      e && typeof e === 'object' && 'response' in e
        ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined
    errors.push(msg || (e instanceof Error ? e.message : 'sync failed'))
    for (const order of toSend) {
      await posStore.resetPendingOrderStatus(order.client_uuid, 'pending')
    }
    return { pushed: 0, errors, rows }
  }
}

export async function pushSingleOrder(
  order: posStore.PendingOfflineOrder
): Promise<{ row?: PushOfflineOrderResult; errors: string[] }> {
  return withPushMutex(async () => {
    const result = await pushOrdersBatch([order])
    const row = result.rows.find((r) => r.client_uuid === order.client_uuid)
    return { row, errors: result.errors }
  })
}

export async function pushPending(): Promise<{ pushed: number; errors: string[] }> {
  return withPushMutex(async () => {
    const pending = await posStore.getPendingOrders()
    if (pending.length === 0) {
      return { pushed: 0, errors: [] }
    }
    const result = await pushOrdersBatch(pending)
    return { pushed: result.pushed, errors: result.errors }
  })
}

export async function syncAllModules(): Promise<void> {
  if (!(await checkConnectivity())) return
  const dispatch = store.dispatch
  const fetches = [
    fetchCategories({}),
    fetchProducts({}),
    fetchCustomers({}),
    fetchSuppliers({}),
    fetchPurchases({}),
    fetchExpenses({}),
    fetchDeliveries({})
  ]
  for (const thunk of fetches) {
    try {
      dispatch(thunk as any)
    } catch {}
  }
}

async function handleOnlineSync(): Promise<void> {
  if (syncing) return
  syncing = true
  try {
    if (!(await checkConnectivity())) return
    try {
      const queueResult = await replayQueue()
      if (queueResult.errors.length) {
        console.warn('Queue replay errors:', queueResult.errors)
      }
    } catch (e) {
      console.warn('Queue replay failed:', e)
    }
    try {
      const tableResult = await flushTableOps()
      if (tableResult.errors.length) {
        console.warn('Table ops flush errors:', tableResult.errors)
      }
    } catch (e) {
      console.warn('Table ops flush failed:', e)
    }
    try {
      const failedBeforePush = await posStore.getFailedCount()
      if (failedBeforePush > 0) {
        await posStore.moveAllFailedOrdersToPending()
      }
      const result = await pushPending()
      if (result.pushed > 0) {
        const scope = posStore.resolveOfflineScope()
        if (scope.branchId && scope.branchId !== 'unknown') {
          await pull(scope.branchId)
          if (typeof window !== 'undefined') window.dispatchEvent(new Event('pos:catalog-updated'))
        }
      }
    } catch (e) {
      console.warn('POS push failed:', e)
    }
    try {
      await publishPosDeviceSyncHeartbeat()
    } catch (e) {
      console.warn('Device sync heartbeat failed:', e)
    }
    try {
      if (shouldRunModuleFanOut()) {
        await syncAllModules()
      }
    } catch (e) {
      console.warn('Sync all modules failed:', e)
    }
  } finally {
    syncing = false
  }
}

export function startAutoSync(): void {
  if (pushIntervalId != null) return

  onlineHandler = () => {
    void handleOnlineSync()
  }
  window.addEventListener('online', onlineHandler)
  const intervalMs = AUTO_SYNC_INTERVAL_MS + Math.floor(Math.random() * AUTO_SYNC_JITTER_MS)
  pushIntervalId = setInterval(() => {
    if (getLastKnownOnline()) {
      void handleOnlineSync()
    }
  }, intervalMs)
  void publishPosDeviceSyncHeartbeat()
}

export function stopAutoSync(): void {
  if (onlineHandler) {
    window.removeEventListener('online', onlineHandler)
    onlineHandler = null
  }
  if (pushIntervalId != null) {
    clearInterval(pushIntervalId)
    pushIntervalId = null
  }
}
